import { useState, useEffect, useCallback } from 'react'
import { Head, router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/Card'
import { Ghost, Check, X, Loader2, ArrowLeft } from 'lucide-react'
import appIcon from '@/assets/images/icon-128.png'

interface NewProps {
  errors?: Record<string, string[]>
}

interface SlugCheckResult {
  available: boolean
  slug: string
  message: string
}

export default function New({ errors }: NewProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [slugStatus, setSlugStatus] = useState<SlugCheckResult | null>(null)
  const [isCheckingSlug, setIsCheckingSlug] = useState(false)

  // Generate slug from name (unless manually edited)
  useEffect(() => {
    if (!slugEdited && name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
      setSlug(generatedSlug)
    }
  }, [name, slugEdited])

  // Debounced slug availability check
  const checkSlugAvailability = useCallback(async (slugToCheck: string) => {
    if (!slugToCheck || slugToCheck.length < 2) {
      setSlugStatus(null)
      return
    }

    setIsCheckingSlug(true)
    try {
      const response = await fetch(`/accounts/check-slug?slug=${encodeURIComponent(slugToCheck)}`)
      const data = await response.json()
      setSlugStatus(data)
    } catch {
      setSlugStatus(null)
    } finally {
      setIsCheckingSlug(false)
    }
  }, [])

  // Check slug availability with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (slug) {
        checkSlugAvailability(slug)
      } else {
        setSlugStatus(null)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [slug, checkSlugAvailability])

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '')
    setSlug(value)
    setSlugEdited(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!slugStatus?.available) return

    setIsSubmitting(true)
    router.post('/accounts', { account: { name, slug } }, {
      onFinish: () => setIsSubmitting(false),
    })
  }

  const canSubmit = name.trim().length > 0 && slugStatus?.available && !isCheckingSlug

  return (
    <>
      <Head title="Create Workspace" />

      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img src={appIcon} alt="Banshee" className="size-16 mx-auto mb-2 rounded-xl" />
            <CardTitle>Create a new workspace</CardTitle>
            <CardDescription>
              A workspace is where your team collaborates with customers
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Workspace name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Acme Inc"
                  autoFocus
                  required
                />
                {errors?.name && (
                  <p className="text-sm text-destructive">{errors.name.join(', ')}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Workspace URL</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">banshee.chat/</span>
                  <div className="relative flex-1">
                    <Input
                      id="slug"
                      value={slug}
                      onChange={handleSlugChange}
                      placeholder="acme-inc"
                      className="pr-10"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {isCheckingSlug ? (
                        <Loader2 className="size-4 animate-spin text-muted-foreground" />
                      ) : slugStatus?.available ? (
                        <Check className="size-4 text-primary" />
                      ) : slugStatus ? (
                        <X className="size-4 text-destructive" />
                      ) : null}
                    </div>
                  </div>
                </div>
                {slugStatus && !isCheckingSlug && (
                  <p className={`text-sm ${slugStatus.available ? 'text-primary' : 'text-destructive'}`}>
                    {slugStatus.message}
                  </p>
                )}
                {errors?.slug && (
                  <p className="text-sm text-destructive">{errors.slug.join(', ')}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full cursor-pointer"
                disabled={!canSubmit || isSubmitting}
                glow="hover"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="size-4 animate-spin" />
                    Creating workspace...
                  </>
                ) : (
                  <>
                    <Ghost className="size-4" />
                    Create workspace
                  </>
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="justify-center">
            <Link
              href="/"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer inline-flex items-center gap-1"
            >
              <ArrowLeft className="size-3" />
              Back to dashboard
            </Link>
          </CardFooter>
        </Card>
      </div>
    </>
  )
}
